import { FunnelIcon } from "@heroicons/react/24/outline";
import { CreditLimitRequestStatus, GetPaginatedCreditLimitRequestsQueryVariables } from "@/graphql/__generated__/graphql-operations";
import { updatePaginationState } from "@/cache/appstate/WriteQueries";
import { usePaginationState } from "@/hooks/cache/appState/usePaginationState";
import { PaginationState } from "@/types";
import { TagItem, RadioButton, FramerButton } from "@/components/shared";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTheme } from "@/context/ThemeProvider";
import { JSX } from "react";
import { Card } from "@/components/ui/card";

type Props = {
  className?: string;
  onFilterSubmit: (_variables: GetPaginatedCreditLimitRequestsQueryVariables) => void;
}

type RadioOption = {
  label: string;
  value: CreditLimitRequestStatus;
}

/**
 * CLRFilterComponent
 *
 * This component provides filtering functionality for assets, including date range and financing status filters.
 *
 * @param {Props} props - The properties for the component.
 * @param {string} props.className - The class name for the component.
 * @param {Function} props.onFilterSubmit - The function to call when the filter is submitted.
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * ```tsx
 *    <CLRFilterComponent
 *      onFilterSubmit={handleFilterSubmit}
 *    />
 * ```
 */
function CLRFilterComponent({onFilterSubmit}: Props): JSX.Element {

  // Pagination
  const paginationState: PaginationState["paginatedCreditLimitRequests"] = usePaginationState("paginatedCreditLimitRequests");
  const { filters, first } = paginationState;


  // Hooks
  const { t } = useTranslation();
  const { darkMode } = useTheme();

  // Constants
  const backgroundColour = darkMode ? "bg-mfdarklighter text-white border-transparent shadow-gray-900": "bg-white";

  const statusOptions: RadioOption[] = [
    { label: "Pending", value: CreditLimitRequestStatus.Pending },
    { label: "Approved", value: CreditLimitRequestStatus.Approved },
    { label: "Denied", value: CreditLimitRequestStatus.Denied },
  ];

  /**
   * OnChange handler for radio buttons
   * @param {string} key
   * @param {string} value
   */
  const onChangeRadioButton = (key: string, value: string) => {
    if (key === "status") {
      updatePaginationState({paginatedCreditLimitRequests: {filters: {status: value as CreditLimitRequestStatus}}});
    }
  };

  /**
   * OnClick handler for reset button
   */
  function handleResetClick() {
    // Call the parent function
    onFilterSubmit({
      first,
      status: null,
    });
    toast.success(t("common:text.filterremoved"), {toastId: "investmentFilterRemoved"});
  }

  /**
   * OnClick handler for submit button
   */
  function handleSubmitClick() {
    onFilterSubmit({
      first,
      status: filters.status,
    });
    toast.success(t("common:text.filterapplied"), {toastId: "investmentFilterApplied"});
  }

  return (
    <div className="flex items-center justify-between mx-4 mb-2 mt-2">
      {/* Filter box */}
      <Popover>
        <PopoverTrigger
          data-cy="clr-filter-popover-trigger"
          className="relative select-none"
        >
          <Card className="flex flex-row item-center h-[36px] w-[300px] px-2 py-1 bg-transparent">
            <div className="mr-2 h-full items-center flex">
              <FunnelIcon
                className="w-5 h-5 dark:text-white shrink-0"
              />
              {
                filters.status === null &&
              <p className="text-sm text-gray-400 pl-2">
                {t("tokentransactions.filters.title")}
              </p>
              }
            </div>
            <div className="flex items-center space-x-2 select-none">
              {
                filters.status !== null &&
              <TagItem label={filters.status as CreditLimitRequestStatus} />
              }
            </div>
          </Card>
        </PopoverTrigger>
        <PopoverContent
          data-cy="clr-filter-popover-content"
          className={`shadow-lg h-auto rounded p-4 z-[200] md:min-w-[300px] mt-1 ${backgroundColour}`}
        >
          {/* Status filters */}
          <p className="font-semibold text-sm mb-1">{t("tokentransactions.filters.status")}</p>
          {statusOptions.map(option => (
            <RadioButton
              key={option.value}
              option={option}
              checked={filters.status === option.value}
              onChange={(value) => onChangeRadioButton("status", value.toString())}
            />
          ))}

          {/* Buttons */}
          <div className="flex items-center justify-between mt-4">
            <FramerButton
              onClick={handleResetClick}
              variant="outline-primary"
            >
              {t("tokentransactions.filters.reset")}
            </FramerButton>
            <FramerButton
              onClick={handleSubmitClick}
              className="px-2 py-1 rounded-md bg-mforange text-white text-sm hover:bg-mforange/80"
            >
              {t("tokentransactions.filters.submit")}
            </FramerButton>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default CLRFilterComponent;
